import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  injectRadioGroupState,
  NgpRadioGroup,
  NgpRadioIndicator,
  NgpRadioItem,
} from 'ng-primitives/radio';

@Component({
  selector: 'tc-radio-group',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    {
      directive: NgpRadioGroup,
      inputs: [
        'ngpRadioGroupDisabled:disabled',
        'ngpRadioGroupOrientation:orientation',
      ],
    },
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioGroupComponent),
      multi: true,
    },
  ],
  host: {
    class: 'grid gap-2',
    '(focusout)': 'onTouched()',
  },
  template: `<ng-content />`,
})
export class RadioGroupComponent implements ControlValueAccessor {
  readonly orientation = input<'horizontal' | 'vertical'>('vertical');
  readonly disabled = input(false, {
    transform: (v: boolean | string) => v === true || v === '',
  });

  private readonly state = injectRadioGroupState<string>();
  private onChange: (value: string | null) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  constructor() {
    this.state().valueChange.subscribe((value) => {
      this.onChange(value);
    });
  }

  writeValue(value: string | null): void {
    this.state().value.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.state().disabled.set(isDisabled || this.disabled());
  }
}

@Component({
  selector: 'tc-radio',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    {
      directive: NgpRadioItem,
      inputs: ['ngpRadioItemValue:radioValue', 'ngpRadioItemDisabled:disabled'],
    },
  ],
  imports: [NgpRadioIndicator],
  host: {
    class:
      'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  },
  template: `
    <span
      ngpRadioIndicator
      class="relative inline-flex h-4 w-4 items-center justify-center rounded-full border border-primary shadow"
    >
      <span
        class="h-2 w-2 rounded-full bg-primary opacity-0 [[data-checked]_&]:opacity-100"
        aria-hidden="true"
      ></span>
    </span>
    <span><ng-content /></span>
  `,
})
export class RadioComponent {
  readonly radioValue = input.required<string>();
  readonly disabled = input(false, {
    transform: (v: boolean | string) => v === true || v === '',
  });
}
